import { Box, AppBar, Container, Toolbar, IconButton, Badge, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import { Logout } from '@mui/icons-material';
import NotificationsActiveIconOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { useFromQueryParam } from '../../../helpers/getFromQueryParam';
import { useElectricPage } from '../../../helpers/isElectricPage';
import { ROUTES } from '../../../constants';
import LogoSection from '../LogoSection';
import { getUnreadNotice } from '../../../api/NoticeApi';
import { useQuery } from 'react-query';
import { isAdminUser, isImpersonating, stopImpersonating } from '../../../helpers/auth';
import { useState } from 'react';
import ImpersonationDialog from '../../../components/common/ImpersonationDialog';
import HeaderIcon from '../../../assets/images/header_icon.svg';
import logoPortalSrc from '../../../assets/images/logo_portal.svg';

const actionButtonStyle = {
    color: 'text.primary',
    textTransform: 'none',
    fontSize: '0.65rem',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 8px',
    minWidth: '56px',
    whiteSpace: 'nowrap',
    borderRadius: 0,
    '& .MuiButton-startIcon': { margin: 0 },
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
};

/** Narrower hit area for mobile header toolbar icons */
const mobileToolbarIconButtonSx = {
    p: 0.375,
    minWidth: 40,
    width: 40,
    maxWidth: 40,
    boxSizing: 'border-box'
};

const Header = ({ isNotPublicPage, onMenuClick, mobileOpen, onLogout }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isElectricPage = useElectricPage();
    const from = useFromQueryParam();
    const profile = useSelector((state) => state.user.profile);
    const history = useHistory();
    const location = useLocation();
    const activeImpersonation = isImpersonating();
    const isAdmin = isAdminUser();
    const [openImpersonation, setOpenImpersonation] = useState(false);
    const normalizedPath = (location.pathname || '/').replace(/\/+$/, '') || '/';
    const isAuthPage = [
        ROUTES.LOGIN,
        ROUTES.FORGET_PASSWORD,
        ROUTES.FORGET_PASSWORD_SENT,
        ROUTES.RESET_PASSWORD,
        ROUTES.SET_PASSWORD
    ].includes(normalizedPath);
    const showFullHeader = !isAuthPage && isNotPublicPage && from !== 'app' && !!profile;

    const { data: unreadData } = useQuery(['getUnreadNotice'], () => getUnreadNotice(), {
        enabled: !!profile && isElectricPage
    });
    const displayCount = unreadData?.count || 0;
    return (
        <AppBar
            position="fixed"
            color="inherit"
            elevation={0}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 3,
                bgcolor: '#fff'
            }}
        >
            {isAdmin && activeImpersonation && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bgcolor: '#d32f2f',
                        color: '#fff',
                        px: 1.5,
                        py: 0.5,
                        borderBottomRightRadius: '4px',
                        zIndex: 10
                    }}
                >
                    <Typography sx={{ fontSize: { xs: '15px', sm: '20px' }, fontWeight: 700, letterSpacing: '0.05em' }}>
                        代理ログイン中
                    </Typography>
                </Box>
            )}
            <Container
                maxWidth={false}
                disableGutters={isAuthPage || showFullHeader}
                sx={isAuthPage ? { px: 2 } : showFullHeader ? { px: 0 } : undefined}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        minHeight: '64px',
                        ...(isAuthPage && {
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%'
                        })
                    }}
                >
                    {isAuthPage ? (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 64
                            }}
                        >
                            <LogoSection disabled={false} variant="portalLogo" />
                        </Box>
                    ) : null}

                    {showFullHeader ? (
                        <>
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    width: {
                                        md: isElectricPage ? 'calc(100% - 247px)' : 'calc(100% + 3px)'
                                    },
                                    alignItems: 'center',
                                    minHeight: 64,
                                    ml: { md: isElectricPage ? '247px' : '-3px' },
                                    boxSizing: 'border-box'
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }} />
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: 900,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        minWidth: 0
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            cursor: 'pointer',
                                            pb: '2px',
                                            flexShrink: 0,
                                            minWidth: 0
                                        }}
                                        onClick={() => history.push(ROUTES.ELECTRIC.BASE)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                history.push(ROUTES.ELECTRIC.BASE);
                                            }
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={HeaderIcon}
                                            alt="しろくま電力"
                                            sx={{
                                                height: '26px',
                                                width: '140px',
                                                display: 'block'
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                ml: 1,
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                lineHeight: 1,
                                                mb: '1px'
                                            }}
                                        >
                                            ポータルサイト
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                                        {isAdmin &&
                                            (activeImpersonation ? (
                                                <Button onClick={stopImpersonating} sx={{ ...actionButtonStyle, color: '#d32f2f' }}>
                                                    <Logout sx={{ fontSize: 24 }} />
                                                    <Typography sx={{ fontSize: 'inherit', fontWeight: 700 }}>代理ログイン終了</Typography>
                                                </Button>
                                            ) : (
                                                <Button onClick={() => setOpenImpersonation(true)} sx={actionButtonStyle}>
                                                    <SwitchAccountIcon sx={{ fontSize: 24 }} />
                                                    <Typography sx={{ fontSize: 'inherit', fontWeight: 700 }}>代理ログイン</Typography>
                                                </Button>
                                            ))}

                                        <Button onClick={() => history.push(ROUTES.ELECTRIC.NOTICE)} sx={actionButtonStyle}>
                                            <Badge
                                                badgeContent={displayCount}
                                                sx={{
                                                    '& .MuiBadge-badge': {
                                                        backgroundColor: '#6ec8e8',
                                                        color: 'white',
                                                        right: -2,
                                                        top: 4,
                                                        height: 18,
                                                        minWidth: 18,
                                                        fontSize: '0.6rem'
                                                    }
                                                }}
                                            >
                                                <NotificationsActiveIconOutlined sx={{ fontSize: 24 }} />
                                            </Badge>
                                            <Typography sx={{ fontSize: 'inherit', fontWeight: 700 }}>お知らせ</Typography>
                                        </Button>
                                        <Button
                                            onClick={() => history.push(`${ROUTES.ELECTRIC.PROCEDURE}?tab=member`)}
                                            sx={actionButtonStyle}
                                        >
                                            <PersonIcon sx={{ fontSize: 24 }} />
                                            会員情報
                                        </Button>
                                        <Button onClick={onLogout} sx={actionButtonStyle}>
                                            <Logout sx={{ fontSize: 24 }} />
                                            <Typography sx={{ fontSize: 'inherit', fontWeight: 700 }}>ログアウト</Typography>
                                        </Button>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: 2, minWidth: 0 }} />
                            </Box>

                            <Box
                                sx={{
                                    display: { xs: 'flex', md: 'none' },
                                    width: 'calc(100% + 3px)',
                                    ml: '-3px',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    pl: 1,
                                    pr: 2,
                                    boxSizing: 'border-box'
                                }}
                            >
                                <Box
                                    onClick={() => history.push(ROUTES.ELECTRIC.BASE)}
                                    sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', minWidth: 0 }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            history.push(ROUTES.ELECTRIC.BASE);
                                        }
                                    }}
                                >
                                    {isElectricPage && isMobile ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                minWidth: 0
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={logoPortalSrc}
                                                alt=""
                                                sx={{
                                                    height: 38,
                                                    width: 'auto',
                                                    maxWidth: 64,
                                                    objectFit: 'contain',
                                                    flexShrink: 0
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'center',
                                                    gap: 0.25,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={HeaderIcon}
                                                    alt=""
                                                    sx={{ height: 18, width: 'auto', display: 'block' }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        color: '#333',
                                                        lineHeight: 1.2,
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    ポータルサイト
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                                                しろくま電力
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#666' }}>
                                                ポータルサイト
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {isAdmin &&
                                        (activeImpersonation ? (
                                            <IconButton
                                                size="small"
                                                onClick={stopImpersonating}
                                                sx={{ ...mobileToolbarIconButtonSx, color: '#d32f2f' }}
                                            >
                                                <Logout sx={{ fontSize: 32 }} />
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                size="small"
                                                onClick={() => setOpenImpersonation(true)}
                                                sx={{ ...mobileToolbarIconButtonSx, color: '#000' }}
                                            >
                                                <SwitchAccountIcon sx={{ fontSize: 32 }} />
                                            </IconButton>
                                        ))}
                                    <IconButton
                                        size="small"
                                        onClick={() => history.push(ROUTES.ELECTRIC.NOTICE)}
                                        sx={{ color: '#000', ...mobileToolbarIconButtonSx }}
                                    >
                                        <NotificationsActiveIconOutlined sx={{ fontSize: 30 }} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => history.push(`${ROUTES.ELECTRIC.PROCEDURE}?tab=member`)}
                                        sx={{ color: '#000', ...mobileToolbarIconButtonSx }}
                                    >
                                        <PersonIcon sx={{ fontSize: 30 }} />
                                    </IconButton>
                                    {isElectricPage && (
                                        <IconButton onClick={onMenuClick} size="small" sx={mobileToolbarIconButtonSx}>
                                            {mobileOpen ? (
                                                <CloseIcon sx={{ color: '#000', fontSize: 30 }} />
                                            ) : (
                                                <MenuIcon sx={{ color: '#000', fontSize: 30 }} />
                                            )}
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        </>
                    ) : null}
                </Toolbar>
            </Container>
            <ImpersonationDialog open={openImpersonation} onClose={() => setOpenImpersonation(false)} />
        </AppBar>
    );
};

export default Header;
